import ducksPondImg from "@/assets/ducks-pond.png";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function About() {
  const ref = useScrollReveal();

  return (
    <section id="about" ref={ref} className="overflow-hidden" style={{ backgroundColor: "#F0EBE0" }}>

      {/* Opening breath */}
      <div className="max-w-2xl mx-auto px-8 pt-32 pb-24 text-center">
        <p className="fade-in text-base tracking-[0.1em] uppercase font-sans font-medium mb-8" style={{ color: "#4E4228" }}>About Rietkuil · Est. 2012</p>
        <h2 className="fade-in delay-200 font-normal leading-tight mb-8" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontSize: "clamp(2.2rem, 4.5vw, 4rem)", color: "#3C3530" }}>
          A place to slow down
        </h2>
        <p className="fade-in delay-300 font-sans font-light leading-loose text-sm" style={{ color: "#6B6350" }}>
          Set on a working farm with cattle, sheep, and maize fields in the Northern Free State, Rietkuil Farmhouse offers an authentic countryside escape just two hours from Johannesburg. The kind of place where you arrive tired and leave restored.
        </p>
      </div>

      {/* Dominant image — bleeds left edge */}
      <div className="flex flex-col lg:flex-row items-stretch">
        <div className="img-reveal lg:w-[38%] overflow-hidden">
          <img
            alt="Wide open Free State farmland at Rietkuil"
            className="w-full h-full object-cover object-center img-washed"
            src="https://storage.readdy-site.link/project_files/613593af-90e6-4fd5-9a7c-ed9cd3136aa5/2426cab4-983e-4ae8-9e6d-44b663421ea6_ChatGPT-Image-Jun-4-2026-11_27_24-AM.png"
          />
        </div>
        {/* Text — sits low, lots of air above */}
        <div className="lg:w-[62%] flex items-start px-12 lg:px-16 py-16 lg:py-20" style={{ backgroundColor: "#F0EBE0" }}>
          <div>
            <p className="fade-in text-base tracking-[0.1em] uppercase font-sans font-medium mb-6" style={{ color: "#4E4228" }}>The Farmhouse</p>
            <h3 className="fade-in delay-100 font-normal leading-snug mb-6" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontSize: "clamp(1.7rem, 2.8vw, 2.6rem)", color: "#3C3530" }}>
              Space for the whole family
            </h3>
            <div className="fade-in delay-200 w-7 h-px mb-7" style={{ backgroundColor: "#A8B09E" }}></div>
            <p className="fade-in delay-300 font-sans font-light leading-loose text-sm mb-10" style={{ color: "#6B6350" }}>
              Four bedrooms and a separate room— comfortably sleeping 10 guests. Whether it&apos;s a family getaway, a group retreat, or a quiet escape from city life, Rietkuil gives you the freedom to simply be.
            </p>
            <div className="fade-in delay-500 grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <p className="text-sm uppercase tracking-[0.08em] font-sans font-medium mb-4" style={{ color: "#4E4228" }}>The House</p>
                <div className="space-y-4">
                  <div>
                    <p className="font-serif font-light text-3xl mb-1" style={{ color: "#6E5E40" }}>4</p>
                    <p className="text-sm uppercase tracking-[0.08em] font-sans font-medium" style={{ color: "#7E7265" }}>Bedrooms</p>
                  </div>
                  <div>
                    <p className="font-serif font-light text-3xl mb-1" style={{ color: "#6E5E40" }}>1</p>
                    <p className="text-sm uppercase tracking-[0.08em] font-sans font-medium" style={{ color: "#7E7265" }}>Bathroom + separate toilet</p>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.08em] font-sans font-medium mb-4" style={{ color: "#4E4228" }}>Garden room</p>
                <div className="space-y-4">
                  <div>
                    <p className="font-serif font-light text-3xl mb-1" style={{ color: "#6E5E40" }}>1</p>
                    <p className="text-sm uppercase tracking-[0.08em] font-sans font-medium" style={{ color: "#7E7265" }}>Bedroom + bathroom</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full-width pause */}
      <div className="max-w-7xl mx-auto px-8 md:px-16 py-24">
        <div className="w-full h-px" style={{ backgroundColor: "#A8B09E" }}></div>
      </div>


      {/* Offset collage */}
      <div className="max-w-7xl mx-auto px-8 md:px-16 py-24">
        <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-0">
          <div className="drift-left lg:w-[30%] overflow-hidden" style={{ height: "380px" }}>
            <img
              alt="Rietkuil farmhouse stoep morning"
              className="w-full h-full object-cover object-top img-washed"
              src="https://storage.readdy-site.link/project_files/613593af-90e6-4fd5-9a7c-ed9cd3136aa5/ffd1b310-507e-455d-b3b0-e813b1910396_ChatGPT-Image-Jun-4-2026-12_02_18-PM.png"
            />
          </div>
          <div className="lg:w-[40%] lg:px-14 flex flex-col justify-center py-6">
            <p className="fade-in text-base tracking-[0.1em] uppercase font-sans font-medium mb-6" style={{ color: "#4E4228" }}>The Experience</p>
            <h3 className="fade-in delay-100 font-normal leading-snug mb-6" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontSize: "clamp(1.7rem, 2.8vw, 2.6rem)", color: "#3C3530" }}>
              Stillness, space<br />&amp; open skies
            </h3>
            <div className="fade-in delay-200 w-7 h-px mb-7" style={{ backgroundColor: "#A8B09E" }}></div>
            <p className="fade-in delay-300 font-sans font-light leading-loose text-sm" style={{ color: "#6B6350" }}>
              No schedules. No noise. Just the farm, the fields, and the freedom to simply be. Wake up to birdsong, spend evenings around the braai, and fall asleep under a sky full of stars — far from the city, close to what matters.
            </p>
          </div>
          <div className="drift-right lg:w-[30%] lg:mt-24 overflow-hidden" style={{ height: "380px" }}>
            <img
              alt="White ducks at the Rietkuil farm pond"
              className="w-full h-full object-cover object-center img-washed"
              src={ducksPondImg}
            />
          </div>
        </div>
      </div>

    </section>
  );
}
